import * as http from "utils/http";
import * as notifications from "utils/notifications";
import { escapeHtml } from "utils/security";
import { parseErrorFromJson } from "utils/responses";
import { BaseRefundRequestModalController } from "./base_refund_request_modal_controller";
import { getRefundRequestModalData, removeRefundRequestModalData } from "./payments_controller";
import { Payment } from "../../types/payment";

export default class extends BaseRefundRequestModalController<Payment> {
  static targets = ["amountInput", "reasonInput", "cancelButton", "createButton", "form", "amountError"];
  static values = {
    paymentId: String,
    paymentStatus: String,
    maxAmount: Number,
  };

  declare readonly amountInputTarget: HTMLInputElement;
  declare readonly reasonInputTarget: HTMLTextAreaElement;
  declare readonly cancelButtonTarget: HTMLButtonElement;
  declare readonly createButtonTarget: HTMLButtonElement;
  declare readonly formTarget: HTMLFormElement;
  declare readonly amountErrorTarget: HTMLElement;
  declare paymentIdValue: string;
  declare paymentStatusValue: string;
  declare maxAmountValue: number;

  connect(): void {
    const modalData = getRefundRequestModalData(this.paymentIdValue);
    this.initializeModal(modalData);
    this.initializeAmountInput();
    this.setupFocusTrap(this.amountInputTarget);
    this.setupEscapeKeyHandler();

    if (this.amountInputTarget) {
      this.amountInputTarget.focus();
    }
  }

  cancel(): void {
    this.handleCancel();
  }

  closeOnOverlayClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (target === this.element || target.classList.contains("modal-overlay")) {
      this.handleCancel();
    }
  }

  private initializeAmountInput(): void {
    if (this.amountInputTarget && this.maxAmountValue) {
      this.amountInputTarget.max = this.maxAmountValue.toString();
      if (!this.amountInputTarget.value) {
        this.amountInputTarget.value = this.maxAmountValue.toString();
      }
    }
  }

  protected handleCancel(): void {
    this.restoreActions();
    this.closeModal();
  }

  private showAmountError(message: string): void {
    this.amountErrorTarget.textContent = message;
    this.amountErrorTarget.classList.remove("hidden");
    this.amountInputTarget.classList.add("border-red-500");
  }

  private clearAmountError(): void {
    this.amountErrorTarget.classList.add("hidden");
    this.amountInputTarget.classList.remove("border-red-500");
  }

  validateAmount(): boolean {
    const amountValue = this.amountInputTarget.value.trim();
    const amount = parseFloat(amountValue);

    this.clearAmountError();

    if (!amountValue || amountValue === "") {
      this.showAmountError("Amount is required");
      return false;
    }

    if (isNaN(amount) || amount <= 0) {
      this.showAmountError("Amount must be greater than 0");
      return false;
    }

    if (amount > this.maxAmountValue) {
      this.showAmountError(`Amount cannot exceed ${escapeHtml(this.maxAmountValue)}`);
      return false;
    }

    return true;
  }

  private getFormValue(fieldName: string): string | null {
    const formData = new FormData(this.formTarget);
    const formKey = `refund_request[${fieldName}]`;
    const value = formData.get(formKey) as string | null;
    return value?.trim() || null;
  }

  private getPaymentId(): string | null {
    return this.paymentIdValue?.trim() || this.getFormValue("payment_id");
  }

  private getPaymentStatus(): string | null {
    return this.paymentStatusValue?.trim() || this.getFormValue("payment_status");
  }

  private getFormData(): {
    amount: number;
    reason: string | null;
    paymentId: string | null;
    paymentStatus: string | null;
  } {
    const amountValue = this.amountInputTarget.value.trim();
    const amount = parseFloat(amountValue);
    const reasonValue = this.reasonInputTarget.value.trim();
    const reason = reasonValue === "" ? null : reasonValue;
    const paymentId = this.getPaymentId();
    const paymentStatus = this.getPaymentStatus();

    return { amount, reason, paymentId, paymentStatus };
  }

  private validateFormData(formData: {
    amount: number;
    reason: string | null;
    paymentId: string | null;
    paymentStatus: string | null;
  }): boolean {
    const INVALID_FORM_DATA_MESSAGE = "Invalid form data. Please check all required fields.";

    if (!formData.paymentId || !formData.paymentStatus || isNaN(formData.amount) || formData.amount <= 0) {
      notifications.alert(INVALID_FORM_DATA_MESSAGE);
      return false;
    }

    return true;
  }

  private validateForm(): boolean {
    const isAmountValid = this.validateAmount();

    if (!isAmountValid || !this.formTarget.checkValidity()) {
      if (isAmountValid && !this.formTarget.checkValidity()) {
        this.amountInputTarget.reportValidity();
      }
      return false;
    }

    const formData = this.getFormData();

    if (!this.validateFormData(formData)) {
      return false;
    }

    return true;
  }

  private buildRequestBody(formData: {
    amount: number;
    reason: string | null;
    paymentId: string | null;
    paymentStatus: string | null;
  }): { refund_request: { payment_id: string; refund_amount: number; reason: string | null; payment_status: string } } {
    return {
      refund_request: {
        payment_id: formData.paymentId!,
        refund_amount: formData.amount,
        reason: formData.reason,
        payment_status: formData.paymentStatus!,
      },
    };
  }

  private setButtonLoading(): void {
    this.createButtonTarget.disabled = true;
    this.createButtonTarget.textContent = "Creating...";
  }

  private setButtonReady(): void {
    this.createButtonTarget.disabled = false;
    this.createButtonTarget.textContent = "Create";
  }

  private updateGridAfterSuccess(data: { refund_request: string }): void {
    if (!this.node) return;

    this.node.setDataValue("refund_request", data.refund_request);
    const currentRefundRequests = this.node.data.refund_requests || [];
    this.node.setDataValue("refund_requests", [...currentRefundRequests, data.refund_request]);
    this.node.setDataValue("actions", "");
    this.refreshGridCell(this.node, "actions");
  }

  protected handleError(errorMessage: string): void {
    this.setButtonReady();
    super.handleError(errorMessage);
  }

  async create(event: Event): Promise<void> {
    event.preventDefault();

    if (!this.validateForm()) {
      return;
    }

    const formData = this.getFormData();
    this.setButtonLoading();

    try {
      const body = this.buildRequestBody(formData);
      const response = await http.httpPost("/payments/refund_requests", body);
      const data = await response.json();

      if (response.ok) {
        notifications.success(data.message);
        this.updateGridAfterSuccess(data);
        this.requestCompleted = true;
        removeRefundRequestModalData(this.paymentIdValue);
        this.closeModal();
      } else {
        const errorMessage = data.error ? parseErrorFromJson(data.error) : "Failed to create refund request";
        this.handleError(errorMessage);
      }
    } catch (error) {
      this.handleError("An error occurred while creating refund request");
    }
  }

  override disconnect(): void {
    super.disconnect();
    if (!this.requestCompleted) {
      removeRefundRequestModalData(this.paymentIdValue);
    }
  }
}
